import { createModuleContext } from '@rebuild/utils/moduleContext';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useCalendarCommon } from './useCalendarCommon';
import { computed, getCurrentInstance } from 'vue';
import { useCommonData } from '@/hooks';
import { useScheduleCore, SCHEDULE_TYPE, SCHEDULE_CUSTOMS_TYPE } from '@/hooks/calendar/useScheduleCore';
import { getUuid } from '@/utils/api';
import moment from 'moment';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';

const MODULE_NAME = 'customs_calendar';

function buildBaseCtx() {
	return {
		inSlideM: false
	};
}

const { provide: provideCurrentContext, use: useCurrentContext } = createModuleContext(MODULE_NAME, buildBaseCtx);

const useCurrentReactive = () => {
	const vm = getCurrentInstance();
	const { getSurveyEntry: showAppointmentBtn } = useCommonData();

	const {
		customs_calendar_recently, // 获取最近的月份时间戳
		customs_calendar_list, // 日历列表
		customs_calendar_schedule // 日程列表
	} = useModuleData();

	// 日历
	const loadCalendarData = () => {
		return customs_calendar_list.refresh();
	};
	const { getCalendarName, initCalendarConfig } = useCalendarCommon();
	const getCalendarData = computed(() => {
		const data = customs_calendar_list.data?.data || [];
		return data
			.map(calendar => {
				calendar.calendarName = getCalendarName(calendar.id);
				return calendar;
			})
			.sort((a, b) => a.sort - b.sort);
	});
	const getLegendItems = computed(() => {
		return getCalendarData.value.map(item => {
			return {
				label: item.calendarName,
				color: item.calendarColor
			};
		});
	});

	// 日程
	const loadScheduleDataWithRange = async (startTime, endTime) => {
		if (!startTime || !endTime) return;

		// 支持范围加载的日程数据方法
		return customs_calendar_schedule.refresh({
			startTime,
			endTime
		});
	};
	const getScheduleData = computed(() => {
		return customs_calendar_schedule.data?.data || [];
	});

	const loadRecentlyScheduleData = async () => {
		return customs_calendar_recently.refresh();
	};
	const getRecentlyScheduleData = computed(() => {
		return customs_calendar_recently.data?.data;
	});

	const cacheNow = moment().startOf('day'); // 获取今天的开始时间
	const isToday = date => {
		const then = moment(date); // 转化输入的日期
		return cacheNow.isSame(then, 'd'); // 比较两者是否在同一天
	};
	const formatCellDay = day => {
		if (isToday(day)) {
			return vm.proxy.$t('enterprise.datepicker.mini-today');
		}
		return moment(day).format('D');
	};

	// 单元格数据处理
	const { calculateDateRange, filterDataByTimeRange } = useScheduleCore();
	const getCellDataByDate = date => {
		const range = calculateDateRange(date, 'day');
		return filterDataByTimeRange(range, getScheduleData.value, false);
	};
	/**
	 * 根据日期生成日程信息
	 * @param {Date} date - 当前单元格日期时间戳
	 * @param {Number} maxCount - 最大展示个数
	 * @returns {Array} - 返回一个数组，包含要渲染的日程对象
	 */
	const generateFullViewCellContent = (date, maxCount) => {
		let cellData = getCellDataByDate(date);
		if (!cellData) return [];

		// 补充唯一id 用于节点匹配
		// PS：这个id并不是数据维度的唯一id
		const rowId = getUuid();
		cellData = cellData
			.map(v => {
				return {
					...v,
					rowId
				};
			})
			.sort((a, b) => a.scheduleStartTime - b.scheduleStartTime);

		if (cellData.length > maxCount) {
			return cellData
				.slice(0, maxCount - 1)
				.concat({ rowId, type: 'non', value: cellData.length - (maxCount - 1), fullSchedule: cellData });
		}
		return cellData.slice(0, maxCount);
	};

	const { viewModule } = useModuleNavigation();
    const handleScheduleAction = scheduleItem => {
    	viewModule('customs_calendar', scheduleItem);
    };

	const renderLinkAction = scheduleItem => {
		const { type, customsType, announcementVO, newsVO } = scheduleItem;
		if (type === SCHEDULE_TYPE.CUSTOMS_CALENDAR) {
			if (customsType === SCHEDULE_CUSTOMS_TYPE.IRSTORE) return true;
			if (customsType === SCHEDULE_CUSTOMS_TYPE.ANNOUNCEMENT && announcementVO) {
				let realLink = announcementVO.comeinLink || announcementVO.sourceLink;
				if (realLink) return true;
			}
			if (customsType === SCHEDULE_CUSTOMS_TYPE.NEWS && newsVO?.url) return true;
		}
		return false;
	};

	return {
		customs_calendar_recently,
		customs_calendar_list,
		customs_calendar_schedule,

		getCalendarData,
		getLegendItems,
		loadCalendarData,
		initCalendarConfig,
		showAppointmentBtn,

		getScheduleData,
		loadScheduleDataWithRange,

		getRecentlyScheduleData,
		loadRecentlyScheduleData,

		isToday,
		formatCellDay,

		generateFullViewCellContent,
		handleScheduleAction,
		renderLinkAction
	};
};

export { provideCurrentContext, useCurrentContext, useCurrentReactive };
